import {
  ChangeEvent,
  ReactElement,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  getDigitsOnly,
  getDisplaySsnValue,
  getNextCursorPosition,
  isDefined,
  mergeClassNameStrings,
  ssnDefaultHelperText,
  ssnDefaultLabel,
} from '@consumer-frontend/nc-utils';

import {
  NcInput,
  NcInputPropsT,
} from '../NcInput';
import { NcVisibilityToggle } from '../NcVisibilityToggle';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/form/ssn-input.scss';

export type NcSsnInputPropsT = Omit<NcInputPropsT, 'type'>;

export const NcSsnInput = ({
  className,
  defaultValue,
  errorMessage,
  helperText = ssnDefaultHelperText,
  label = ssnDefaultLabel,
  onChange,
  name,
  value,
  ...props
}: NcSsnInputPropsT): ReactElement => {
  const initialDigits = getDigitsOnly(String(value ?? defaultValue ?? ''));
  const [isMasked, setIsMasked] = useState<boolean>(false);
  const [digits, setDigits] = useState<string>(initialDigits);
  const isControlled = isDefined(value);
  const currentDigits = isControlled ? getDigitsOnly(String(value)) : digits;
  const displayValue = getDisplaySsnValue(currentDigits);

  const inputRef = useRef<HTMLInputElement>(null);
  const cursorRef = useRef<number | null>(null);

  // Restore the input cursor after display formatting so caret position stays stable while typing.
  useLayoutEffect(() => {
    if (cursorRef.current === null) { return; }

    const nextCursorPosition = cursorRef.current;

    inputRef.current?.setSelectionRange(nextCursorPosition, nextCursorPosition);
  }, [displayValue]);

  const handleToggleMask = useCallback((): void => {
    setIsMasked((previousMaskState) => !previousMaskState);
  }, []);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const {
        nextCursorPosition,
        nextDigits,
      } = getNextCursorPosition(
        event.target.value,
        event.target.selectionStart ?? event.target.value.length,
      );

      cursorRef.current = nextCursorPosition;

      if (!isControlled) { setDigits(nextDigits); }

      if (!isDefined(onChange)) { return; }

      onChange({
        target: {
          name,
          value: nextDigits,
        },
      } as ChangeEvent<HTMLInputElement>);
    },
    [
      isControlled,
      name,
      onChange,
    ],
  );

  return (
    <NcInput
      {...props}
      autoComplete="off"
      className={mergeClassNameStrings('nc-ssn-input', className)}
      errorMessage={errorMessage}
      helperText={helperText}
      label={label}
      name={name}
      onChange={handleInputChange}
      ref={inputRef}
      suffix={(
        <NcVisibilityToggle
          hideLabel="Hide Social Security Number"
          isMasked={isMasked}
          onToggle={handleToggleMask}
          showLabel="Show Social Security Number"
        />
      )}
      type={isMasked ? 'password' : 'tel'}
      value={displayValue}
    />
  );
};
